/**
 * Making a repeating fabric survive being folded onto a body.
 *
 * A tile laid across the template in one pass is continuous *in the template*,
 * and the template is a UV atlas — so continuity there buys nothing. What has to
 * line up is the pairs of edges that meet on the avatar.
 *
 * Measuring the atlas turns that into two separate, small defects:
 *
 *   **Gutter drift.** The four side faces of a strip *are* laid out in the right
 *   circular order — torso reads right, front, left, back, and each of those
 *   pairs really does meet on the body. But Roblox separates them with a 2px
 *   gutter, so a pattern drawn straight across gains 2px of phase at every
 *   boundary and is 6px out by the time it comes back around.
 *
 *   **The wrap seam.** The torso is 384px around (64+128+64+128) and a limb is
 *   256. A tile only closes if its width divides that. At the default 90px it
 *   does not — 384/90 is 4.27 repeats — so the last face meets the first
 *   mid-motif. This is the one you see: a check that lines up on three seams
 *   and visibly jumps on the fourth.
 *
 * Both are fixed here and nowhere else, so the fill stays a fill.
 *
 * **Not fixed: the caps.** `up` and `down` are horizontal surfaces and a truly
 * continuous mapping onto them needs a rotation, not an offset. They inherit
 * the front face's phase, which makes the front-to-shoulder fold line up and
 * leaves the other three cap edges approximate. That is the right trade for a
 * surface nobody looks at from above.
 */
import type { RegionId } from "../../shared/format";
import { REGIONS } from "../../shared/regions";

/**
 * The four side faces of each strip, in the order they wrap around the body.
 *
 * Not alphabetical and not the atlas order by accident: the left limb's strip is
 * the right limb's rotated by one position, which is the detail that makes naive
 * mirroring wrong and is called out in the measured region map's own notes.
 */
const STRIPS: { faces: RegionId[]; caps: RegionId[] }[] = [
  {
    faces: ["torso.right", "torso.front", "torso.left", "torso.back"],
    caps: ["torso.up", "torso.down"],
  },
  {
    faces: ["rightLimb.left", "rightLimb.back", "rightLimb.right", "rightLimb.front"],
    caps: ["rightLimb.up", "rightLimb.down"],
  },
  {
    faces: ["leftLimb.front", "leftLimb.left", "leftLimb.back", "leftLimb.right"],
    caps: ["leftLimb.up", "leftLimb.down"],
  },
];

export interface SeamPlacement {
  /** How far around the body this face starts, in template pixels. */
  bodyX: number;
  /** The full distance around the strip this face belongs to. */
  circumference: number;
  /**
   * How far DOWN the body this face starts, in template pixels.
   *
   * The vertical twin of `bodyX`, and it exists for the same defect. The atlas
   * stacks a strip's `up` cap, its side row and its `down` cap in one column
   * with a 2px gutter between each, so a pattern drawn straight down gains 2px
   * of phase at every horizontal boundary exactly as it did at every vertical
   * one. `phaseFor` has always removed the horizontal drift and nothing removed
   * this, which is why side-to-side seams came out clean and cap-to-side seams
   * did not.
   *
   * The side row is the origin at 0: all four of its faces share a template row
   * and must keep the same vertical phase, or fixing the fold to the cap would
   * break the folds to each other.
   */
  bodyY: number;
}

/**
 * Where every region sits *on the body*, rather than in the atlas.
 *
 * Built from the measured geometry rather than written down, so it cannot drift
 * away from `template-regions.json` the way a second copy of the numbers would.
 */
export const PLACEMENT: Record<string, SeamPlacement> = (() => {
  const out: Record<string, SeamPlacement> = {};
  for (const strip of STRIPS) {
    const circumference = strip.faces.reduce((sum, id) => sum + REGIONS[id].w, 0);
    let bodyX = 0;
    const start: Record<string, number> = {};
    /*
      The side row is the vertical origin.

      Every face in it shares one template row, so they must share one vertical
      phase — giving `front` its own would tear the folds it makes with `right`,
      `left` and `back`, which is the seam that already works.
    */
    for (const id of strip.faces) {
      start[id] = bodyX;
      out[id] = { bodyX, circumference, bodyY: 0 };
      bodyX += REGIONS[id].w;
    }
    // The caps sit directly above and below the front face in the atlas and
    // share its column, so inheriting its phase is what makes the fold work.
    const front = strip.faces.find((id) => id.endsWith(".front"));
    const side = front ? REGIONS[front] : null;
    for (const cap of strip.caps) {
      /*
        Above the row or below it, in BODY distance rather than atlas distance.

        `up` ends where the side row begins, so it starts a cap's height above
        the origin; `down` starts where the side row ends. Both are exact — the
        only thing being removed is the gutter the atlas inserted.
      */
      const bodyY = cap.endsWith(".up") ? -REGIONS[cap].h : (side?.h ?? 0);
      out[cap] = { bodyX: front ? (start[front] ?? 0) : 0, circumference, bodyY };
    }
  }
  return out;
})();

/**
 * The nearest tile width that closes around a strip.
 *
 * A tile that does not divide the circumference cannot line up with itself, no
 * matter where the phase starts — so the size is snapped rather than the seam
 * being fudged. The torso and the limbs have different circumferences and so
 * snap differently; a few percent of difference in weave size between a sleeve
 * and a chest is invisible, and a broken seam is not.
 *
 * Never returns zero: at least one repeat has to go round.
 */
export function snapTile(tilePx: number, circumference: number): number {
  if (!(tilePx > 0) || !(circumference > 0)) return tilePx;
  const repeats = Math.max(1, Math.round(circumference / tilePx));
  return circumference / repeats;
}

/**
 * How far to shift the pattern for one region, in template pixels.
 *
 * The pattern is sampled at `x - offset`, so this is exactly the difference
 * between where the face sits in the atlas and where it sits on the body. It is
 * what removes the accumulated gutter.
 */
export function phaseFor(id: RegionId): number {
  const place = PLACEMENT[id];
  if (!place) return 0;
  return REGIONS[id].x - place.bodyX;
}

/**
 * How far to shift the pattern vertically for one region, in template pixels.
 *
 * The twin of `phaseFor`. Sampled at `y - phaseY`, so this is the difference
 * between where the face sits in the atlas and where it sits down the body —
 * which is what removes the gutter that accumulates between a strip's `up` cap,
 * its side row and its `down` cap.
 *
 * **Not snapped, where the width is.** `tileFor` rounds the tile so a whole
 * number of repeats closes around a strip, because a strip closes on itself and
 * a mismatch is a visible jump. A strip does not close vertically — the atlas
 * cannot even represent the back face's share of that ring — so there is
 * nothing to close, and snapping the height too would scale the weave
 * differently in each axis and distort it for no gain.
 */
export function phaseYFor(id: RegionId): number {
  const place = PLACEMENT[id];
  if (!place) return 0;
  return REGIONS[id].y - place.bodyY;
}

/** The snapped tile width for the strip a region belongs to. */
export function tileFor(id: RegionId, tilePx: number): number {
  const place = PLACEMENT[id];
  return place ? snapTile(tilePx, place.circumference) : tilePx;
}
